import React from 'react';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import { Switch, Route, useLocation, matchPath } from 'react-router-dom';
import { createHashHistory } from 'history';
import { AuthContext, DataProviderContext, TranslationProvider,
    AdminUI, Resource, NotFound, Loading,
    useRedirect, usePermissions } from 'react-admin';

import createAdminStore from './createAdminStore';
import i18nProvider from './i18n/i18nProvider';
import authProvider from './authProvider';
import dataProvider from './dataProvider';
import Layout from './Layout';
import LoginPage from './containers/login';
import schools from './containers/schools';
import supervisors, { EditMe } from './containers/supervisors';
import players from './containers/players';
import lessonGroups from './containers/lesson_groups';
import studentFiles from './containers/student_files';
import passwordForm from './containers/password_form';
import PasswordReset from './containers/password_reset';
import PasswordResetMail from './containers/password_reset_mail';

const RedirectPasswordForm = () => {
  const redirect = useRedirect();
  const { loaded, permissions } = usePermissions();
  if (loaded && permissions) {
    if (permissions.isFirstLogin) {
      redirect('/profiles/create');
    } else {
      window.location = '/';
    }
  }
  return (<Loading />);
};

const routes = [
  <Route exact path="/me" component={EditMe} />
]
const openRoutes = [
  <Route exact path="/password_reset_mail" component={PasswordResetMail} noLayout />,
  <Route exact path="/password_reset/:h/:p/:s" component={PasswordReset} noLayout />
]

const history = createHashHistory();
const store = createAdminStore({
  authProvider,
  dataProvider,
  history,
});

const title = 'Life is Tech! Lesson';

const AuthUI = () => {
  const first = authProvider.isFirstLogin();
  const dashboard = first ? RedirectPasswordForm : null;
  const catchAll = first ? RedirectPasswordForm : NotFound;
  return (
    <AuthContext.Provider value={authProvider}>
      <AdminUI
        title={title}
        loginPage={LoginPage}
        layout={Layout}
        dashboard={dashboard}
        catchAll={catchAll}
        customRoutes={routes}
      >
        {permissions => {
          const first = permissions ? permissions.isFirstLogin : true;
          const owner = permissions ? permissions.isOwner : true;
          const lessonGroupProps = owner ? lessonGroups : { list: NotFound, icon: lessonGroups.icon }
          return [
            !first ? <Resource name="players" {...players} /> : null,
            !first ? <Resource name="supervisors" {...supervisors} /> : null,
            !first ? <Resource name="lesson_groups" {...lessonGroupProps} /> : null,
            owner ? <Resource name="schools" {...schools} /> : null,
            !first ? <Resource name="student_files" {...studentFiles} /> : null,
            first ? <Resource name="profiles" {...passwordForm} /> : null
          ];
        }}
      </AdminUI>
    </AuthContext.Provider>
  );
}

const OpenUI = () => {
  return (
    <AuthContext.Provider>
      <AdminUI
        title={'open'}
        catchAll={NotFound}
        customRoutes={openRoutes}
      >
        <Resource />
      </AdminUI>
    </AuthContext.Provider>
  );
}

const SwitchAuth = () => {
  const location = useLocation();
  const match= openRoutes.find(r => matchPath(location.pathname, r.props));
  if (match) {
    return <OpenUI />
  }
  return <AuthUI />
}

const App = () => {
  return (
    <Provider store={store}>
      <ConnectedRouter history={history}>
        <Switch>
          <DataProviderContext.Provider value={dataProvider}>
            <TranslationProvider i18nProvider={i18nProvider}>
              <SwitchAuth />
            </TranslationProvider>
          </DataProviderContext.Provider>
        </Switch>
      </ConnectedRouter>
    </Provider>
  );
};

export default App;
